/**
 * JustificationAnalysisPanel Component
 *
 * Hosts two independent LLM conversations for the selected fault tree node,
 * switched via sub-tabs:
 *   - "CFR Analysis": a regulatory gap analysis seeded by an automatic Daedalus
 *     CFR lookup. Runs once per node (eagerly, on node select) when the node has
 *     at least one mapped report; can be re-run manually.
 *   - "Chat": free-form Q&A over the AI justifications (the original behaviour).
 *
 * Each thread persists separately per node in the workbook store, so switching
 * tabs or nodes preserves both conversations.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Checkbox, CircularProgress, FormControlLabel, Tab, Tabs, Typography} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {IrpMappedReportDTO} from '../../../../../../@types/integrated-risk-picture';
import {FaultTreeNode} from '../../../../../../@types/faultTreeNode';
import {CHAT_CHART_PROMPT} from '../../../../../common/ChatChart';
import LLMChatDialog, {ChatMessage} from '../../../../../common/LLMChatDialog';
import CfrService, {DAEDALUS_RESULTS_MARKER} from '../../../../../../services/cfr.service';
import {
    PersistedChatMessage,
    useIRPWorkbookStore,
    useIRPWorkbookStoreApi,
    useNodeChatContextSent,
    useNodeChatHistory,
    useNodeCfrHistory,
    useNodeCfrStatus,
} from '../../../../../../stores/irp-workbook.store';

/**
 * Props for JustificationAnalysisPanel
 */
export interface JustificationAnalysisPanelProps {
    reports: IrpMappedReportDTO[];
    node: FaultTreeNode | null;
    loading?: boolean;
    /** When true, fills the parent container height instead of using maxHeight */
    fillHeight?: boolean;
}

type SubTab = 'cfr' | 'chat';

/**
 * Display noun for a fault tree node, used in the chat title and suggested
 * prompts: barriers read as "control", precursors as "precursor", and anything
 * else (gates etc.) as the neutral "node".
 */
const nodeNounFor = (node: FaultTreeNode | null): string => {
    switch (node?.nodeClass) {
        case 'BARRIER':
            return 'control';
        case 'PRECURSOR':
            return 'precursor';
        default:
            return 'node';
    }
};

/** Example starter questions shown as clickable chips in the general Chat thread. */
const CONTROL_SUGGESTED_PROMPTS = [
    'What are the top 5 reasons this control fails?',
    'Are there any obvious patterns in why this control fails?',
    'Has this control got better or worse over time?',
];

const PRECURSOR_SUGGESTED_PROMPTS = [
    'What are the top 5 reasons this precursor occurs?',
    'Are there any obvious patterns in when this precursor occurs?',
    'Has this precursor become more or less frequent over time?',
];

const NODE_SUGGESTED_PROMPTS = [
    'What are the top 5 reasons this node fails?',
    'Are there any obvious patterns in why this node fails?',
    'Has this node got better or worse over time?',
];

/** Starter questions matching the selected node's class. */
const suggestedPromptsFor = (node: FaultTreeNode | null): string[] => {
    switch (node?.nodeClass) {
        case 'BARRIER':
            return CONTROL_SUGGESTED_PROMPTS;
        case 'PRECURSOR':
            return PRECURSOR_SUGGESTED_PROMPTS;
        default:
            return NODE_SUGGESTED_PROMPTS;
    }
};

/**
 * Default cap on how many justifications are sent to the LLM. Nodes at or below this
 * send everything; above it the context is capped here unless the user opts to send all.
 */
const REPORT_LIMIT = 500;

/** Format a report dateTime as a UTC ISO date (YYYY-MM-DD) for LLM context, or a fallback. */
const formatIsoDate = (value: string | null | undefined): string => {
    if (!value) return 'date unknown';
    const date = new Date(value);
    return isNaN(date.getTime()) ? 'date unknown' : date.toISOString().slice(0, 10);
};

/**
 * Panel that analyzes justifications using LLM with chat interface
 */
const JustificationAnalysisPanel: React.FC<JustificationAnalysisPanelProps> = ({
    reports,
    node,
    loading = false,
    fillHeight = false
}) => {
    const storeApi = useIRPWorkbookStoreApi();
    const cfrPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Chat is the default tab; the CFR analysis runs in the background (the auto-run
    // effect is independent of the active tab) since it can take a while to complete.
    const [activeSubTab, setActiveSubTab] = useState<SubTab>('chat');

    // Opt-in to send every justification to the LLM even when the node exceeds
    // REPORT_LIMIT. Reset per node so a large node never silently inherits "send all".
    const [includeAllReports, setIncludeAllReports] = useState(false);

    // Clear progress interval on unmount (tab switch, node change, etc.)
    useEffect(() => {
        return () => {
            if (cfrPollIntervalRef.current) {
                clearInterval(cfrPollIntervalRef.current);
            }
        };
    }, []);

    // Get node ID as string for store keys
    const nodeId = node?.id ? String(node.id) : null;

    // Reset the "send all" opt-in whenever the selected node changes.
    useEffect(() => {
        setIncludeAllReports(false);
    }, [nodeId]);

    // Persisted general-chat state from store
    const persistedMessages = useNodeChatHistory(nodeId);
    const persistedContextSent = useNodeChatContextSent(nodeId);
    const setNodeChatHistory = useIRPWorkbookStore((state) => state.setNodeChatHistory);
    const setNodeChatContextSent = useIRPWorkbookStore((state) => state.setNodeChatContextSent);

    // Persisted CFR-thread state from store
    const cfrMessages = useNodeCfrHistory(nodeId);
    const cfrStatus = useNodeCfrStatus(nodeId);
    const setNodeCfrHistory = useIRPWorkbookStore((state) => state.setNodeCfrHistory);

    // Handler to update persisted general-chat messages
    const handleMessagesChange = useCallback((messages: ChatMessage[]): void => {
        if (nodeId) {
            setNodeChatHistory(nodeId, messages as PersistedChatMessage[]);
        }
    }, [nodeId, setNodeChatHistory]);

    // Handler to update persisted context sent flag
    const handleContextSentChange = useCallback((sent: boolean): void => {
        if (nodeId) {
            setNodeChatContextSent(nodeId, sent);
        }
    }, [nodeId, setNodeChatContextSent]);

    // Handler to update persisted CFR-thread messages (follow-up questions)
    const handleCfrMessagesChange = useCallback((messages: ChatMessage[]): void => {
        if (nodeId) {
            setNodeCfrHistory(nodeId, messages as PersistedChatMessage[]);
        }
    }, [nodeId, setNodeCfrHistory]);

    // Filter to only reports with justifications
    const reportsWithJustifications = useMemo(() => {
        return reports.filter(r => r.justification && r.justification.trim().length > 0);
    }, [reports]);

    // Separate reports into effective (positive) and ineffective (negative) interactions
    const { effectiveReports, ineffectiveReports } = useMemo(() => {
        const effective = reportsWithJustifications.filter(r => r.positiveInteraction === true);
        const ineffective = reportsWithJustifications.filter(r => r.positiveInteraction !== true);
        return { effectiveReports: effective, ineffectiveReports: ineffective };
    }, [reportsWithJustifications]);

    // Create context to prepend to user's first message in the general chat
    const contextPrepend = useMemo(() => {
        if (!node || reportsWithJustifications.length === 0) {
            return '';
        }

        const nodeName = node.name || 'Unknown Node';
        const nodeDescription = node.description || '';
        const statementOfTruth = node.statementOfTruth || '';

        // Send every justification by default. Above REPORT_LIMIT, cap the total unless
        // the user opted to send all; failures are prioritised when the cap is hit.
        const limited = reportsWithJustifications.length > REPORT_LIMIT && !includeAllReports;
        const ineffCount = limited ? Math.min(ineffectiveReports.length, REPORT_LIMIT) : ineffectiveReports.length;
        const effCount = limited ? Math.max(0, REPORT_LIMIT - ineffCount) : effectiveReports.length;

        // Format justifications for a group. Each line carries the report date and
        // source so the model can reason about trends over time and across sources.
        const formatJustifications = (groupReports: typeof reportsWithJustifications, maxCount: number): string => {
            return groupReports
                .slice(0, maxCount)
                .map((report, index) =>
                    `${index + 1}. [${formatIsoDate(report.dateTime)} | ${report.sourceName || 'Unknown source'}] ${report.justification}`
                )
                .join('\n\n');
        };

        // Build the context with separated groups
        let context = `Context: Fault Tree Node Analysis

Node Name: ${nodeName}
${nodeDescription ? `Description: ${nodeDescription}\n` : ''}${statementOfTruth ? `Statement of Truth: "${statementOfTruth}"\n` : ''}`;

        if (limited) {
            context += `
NOTE: This node has ${reportsWithJustifications.length} reports with justifications. To stay within size limits only ${ineffCount + effCount} are included below (failures prioritised).
`;
        } else {
            context += '\n';
        }

        // Add ineffective (barrier failure) justifications
        if (ineffectiveReports.length > 0) {
            const label = ineffCount < ineffectiveReports.length
                ? `showing ${ineffCount} of ${ineffectiveReports.length} reports`
                : `${ineffectiveReports.length} reports`;
            context += `
BARRIER INEFFECTIVE - Control Failed (${label}):
These reports describe incidents where the barrier/control "${nodeName}" FAILED to prevent the hazard.

${formatJustifications(ineffectiveReports, ineffCount)}
`;
        }

        // Add effective (barrier success) justifications
        if (effectiveReports.length > 0 && effCount > 0) {
            const label = effCount < effectiveReports.length
                ? `showing ${effCount} of ${effectiveReports.length} reports`
                : `${effectiveReports.length} reports`;
            context += `
BARRIER EFFECTIVE - Control Succeeded (${label}):
These reports describe incidents where the barrier/control "${nodeName}" SUCCESSFULLY prevented or mitigated the hazard.

${formatJustifications(effectiveReports, effCount)}
`;
        }

        context += `
---

${CHAT_CHART_PROMPT}

---

User Question: `;

        return context;
    }, [node?.id, node?.name, node?.description, node?.statementOfTruth, reportsWithJustifications, effectiveReports, ineffectiveReports, includeAllReports]);

    // Context message for the general chat - include node ID to ensure it changes when node changes
    const contextMessage = useMemo(() => {
        if (reportsWithJustifications.length === 0) {
            return 'No AI justifications available for analysis';
        }
        const parts = [];
        if (ineffectiveReports.length > 0) {
            parts.push(`${ineffectiveReports.length} ineffective`);
        }
        if (effectiveReports.length > 0) {
            parts.push(`${effectiveReports.length} effective`);
        }
        return `Analyzing ${parts.join(' + ')} barrier interactions for: ${node?.name || 'Unknown'}`;
    }, [reportsWithJustifications.length, ineffectiveReports.length, effectiveReports.length, node?.id, node?.name]);

    /**
     * Poll a backend CFR analysis run, writing real status and streamed partial
     * text into the per-node CFR thread. Writes go through the store so the run
     * stays resumable when the user switches tabs or views mid-run.
     */
    const pollAnalysisRun = useCallback((pollNodeId: string, runId: string): void => {
        if (cfrPollIntervalRef.current) {
            clearInterval(cfrPollIntervalRef.current);
            cfrPollIntervalRef.current = null;
        }
        const poll = async (): Promise<void> => {
            try {
                const snapshot = await CfrService.getAnalysisSnapshot(runId);
                const api = storeApi.getState();
                if (snapshot.done) {
                    if (cfrPollIntervalRef.current) {
                        clearInterval(cfrPollIntervalRef.current);
                        cfrPollIntervalRef.current = null;
                    }
                    if (snapshot.error || !snapshot.result) {
                        api.setNodeCfrHistory(pollNodeId, [
                            { role: 'system', content: `CFR lookup failed: ${snapshot.error || 'No summary generated'}` }
                        ] as PersistedChatMessage[]);
                        api.setNodeCfrStatus(pollNodeId, 'error');
                    } else {
                        api.setNodeCfrHistory(pollNodeId, [
                            { role: 'system', content: 'Querying Daedalus...done. Summarizing regulatory evaluations...done.' },
                            { role: 'assistant', content: snapshot.result }
                        ] as PersistedChatMessage[]);
                        api.setNodeCfrStatus(pollNodeId, 'done');
                    }
                    return;
                }
                const statusLine = snapshot.detail
                    || (snapshot.phase === 'SEARCHING_REGULATIONS'
                        ? 'Querying Daedalus...'
                        : 'Summarizing regulatory evaluations...');
                const thread: PersistedChatMessage[] = [{ role: 'system', content: statusLine }];
                if (snapshot.partialText) {
                    thread.push({ role: 'assistant', content: snapshot.partialText });
                }
                api.setNodeCfrHistory(pollNodeId, thread);
            } catch (error: any) {
                // The run is gone (server restart or eviction): stop and surface it.
                // Transient poll failures fall through silently and retry next tick.
                if (error?.response?.status === 404) {
                    if (cfrPollIntervalRef.current) {
                        clearInterval(cfrPollIntervalRef.current);
                        cfrPollIntervalRef.current = null;
                    }
                    const api = storeApi.getState();
                    api.setNodeCfrHistory(pollNodeId, [
                        { role: 'system', content: 'CFR analysis is no longer available on the server. Use Re-run to start a new one.' }
                    ] as PersistedChatMessage[]);
                    api.setNodeCfrStatus(pollNodeId, 'error');
                }
            }
        };
        void poll();
        cfrPollIntervalRef.current = setInterval((): void => { void poll(); }, 2500);
    }, [storeApi]);

    /**
     * Run the CFR lookup for the current node: build the search query and the
     * analysis prompt, start a backend run, then poll it for status / partial
     * text / result. Writes go through the store (not component state) so a
     * result still lands if the user switches tabs mid-run.
     *
     * @param force when true, re-runs even if a run already completed/started.
     */
    const runCfrLookup = useCallback(async (force: boolean): Promise<void> => {
        if (!node || !nodeId) return;

        const api = storeApi.getState();
        const existingStatus = api.getNodeCfrStatus(nodeId);
        // Guard against duplicate auto-runs (StrictMode double-invoke, re-render, revisit).
        if (!force && existingStatus !== undefined) return;
        if (existingStatus === 'running') return;

        api.setNodeCfrStatus(nodeId, 'running');

        // Local running copy of the thread; each step replaces the store value.
        let thread: ChatMessage[] = [{ role: 'system', content: 'Querying Daedalus...' }];
        api.setNodeCfrHistory(nodeId, thread as PersistedChatMessage[]);

        if (cfrPollIntervalRef.current) {
            clearInterval(cfrPollIntervalRef.current);
            cfrPollIntervalRef.current = null;
        }

        try {
            // The whole pipeline (Daedalus search + streaming LLM summarization) runs on
            // the backend as one resumable run; this query mirrors the old
            // searchForNodeRegulations composition.
            const searchQuery = [node.name || '', node.statementOfTruth, node.description]
                .filter((part): part is string => Boolean(part))
                .join(' ');

            // Build context about barrier effectiveness from our data, including justifications
            const formatJustificationsForPrompt = (groupReports: typeof ineffectiveReports, max: number): string => {
                return groupReports
                    .slice(0, max)
                    .map(r => `- "${r.justification}"`)
                    .join('\n');
            };

            let dataContext = '';
            if (ineffectiveReports.length > 0) {
                dataContext += `\nBARRIER FAILURES (${ineffectiveReports.length} incidents where this control FAILED):
${formatJustificationsForPrompt(ineffectiveReports, 15)}
`;
            }
            if (effectiveReports.length > 0) {
                dataContext += `\nBARRIER SUCCESSES (${effectiveReports.length} incidents where this control SUCCEEDED):
${formatJustificationsForPrompt(effectiveReports, 10)}
`;
            }

            const summarizationPrompt = `You are a senior aviation safety expert performing a regulatory gap analysis. Your audience is highly experienced aviation professionals (DERs, ACO engineers, Part 121 safety managers, NTSB investigators). Be technical and precise—do not dumb anything down.

CONTEXT:
Safety barrier/control: "${node.name || 'Unknown'}"
${node.statementOfTruth ? `Purpose: "${node.statementOfTruth}"` : ''}
${node.description ? `Description: "${node.description}"` : ''}

INCIDENT DATA - GROUND TRUTH:
These AI-generated justifications describe WHY this barrier failed or succeeded in real incidents.
${dataContext}

REGULATIONS FROM DAEDALUS:
${DAEDALUS_RESULTS_MARKER}

GROUNDING RULES (apply to the entire analysis):
- Do NOT identify responsible FAA offices, departments, or organizational codes anywhere in this analysis. The FAA's organization has changed and office routing is handled outside this document.
- Quote regulatory text verbatim ONLY when that text appears in the Daedalus results above. For regulations you add from your own knowledge, give the citation and title and describe the operative requirements in your own words, marking the entry [Not verified against source text]. Never present remembered text inside quote marks.
- State any data limitations ONCE, up front in the reader context, then analyze without repeating the disclaimer in every section.
- This is a standalone report, not a chat reply: no offers of follow-up work, no conversational asides.

---

YOUR ANALYSIS:

## 1. APPLICABLE REGULATIONS

List ALL regulations that are relevant to this barrier/control and the type of operations reflected in the incident data. For EACH regulation:

1. **Citation**: Full CFR citation (e.g., 14 CFR §91.113)
2. **Title**: Official title of the regulation
3. **Full Text or Key Provisions**: Quote the operative regulatory text verbatim when it is present in the Daedalus results. For regulations added from your own knowledge, describe the operative requirements in your own words and mark the entry [Not verified against source text] - never put remembered text in quote marks.
4. **Relevance to This Barrier**: Explain specifically how this regulation relates to the barrier/control function.

Include regulations from the Daedalus results if applicable, but ALSO include any other regulations you know are relevant to this barrier type that were NOT in the Daedalus results. Be comprehensive—the goal is a complete regulatory picture for this control.

CRITICAL: Only include regulations that ACTUALLY apply to the type of operations in the incident data. If the incidents involve manned aircraft operations, do not include Part 107 (UAS) regulations. If they involve commercial operations, focus on Parts 121/135 rather than Part 91.

Do not write a full entry for a regulation only to conclude it is inapplicable. Instead, close this section with a single "Considered and excluded" line listing any such regulations with a brief reason each.

## 2. SYSTEMIC WEAKNESSES

Identify deficiencies in the regulatory and procedural framework ITSELF—where the system as designed is inadequate to prevent the failures observed in the incident data.

**LITMUS TEST for Systemic vs. Implementation**: If every person followed the regulations perfectly, would the accident still have happened?
- **YES** → SYSTEMIC WEAKNESS (the rules themselves are the problem)
- **NO** → IMPLEMENTATION WEAKNESS (the execution failed, not the rules)

CRITICAL: Report weaknesses supported by the incident evidence. If no systemic weaknesses are evident, state "No systemic weaknesses identified"
and explain why. If many are evident - which is likely - list all of them. The count must be driven entirely by what the data supports—not by
what seems like a "reasonable" number. There is no upper limit.

For each weakness identified:

1. **Weakness Title**: A concise label (e.g., "No regulation addresses multi-position ATC staffing limits")

2. **Weakness Description**: A detailed technical explanation of the deficiency. Do NOT just restate the title. Explain the specific regulatory gap, why it exists, and how it permits the observed failures. This must be detailed enough that an aviation SME could draft a corrective action. Examples of deficiency types:
   - No regulation addresses this failure mode
   - Regulation exists but contains ambiguity that permits unsafe practices
   - Regulatory requirements are insufficient for the operational environment
   - Conflicting requirements between regulations create compliance uncertainty
   - Guidance material (ACs, InFOs) is outdated or contradicts current operational practices

3. **Evidence from Incidents**: Cite ALL incident justifications that support this weakness. If multiple incidents demonstrate the same systemic issue, quote or reference each one. Pattern strength matters—a weakness supported by 8 incidents is more significant than one supported by 1.

4. **Regulatory Context**: For each regulation cited, explain specifically WHY it is relevant and HOW the weakness relates to it. Do not simply list "14 CFR §91.123"—explain what §91.123 requires, what gap or ambiguity exists, and why that gap permits the observed failures. If no regulation exists, explain what regulatory authority SHOULD exist and where it would fit in the CFR structure.

## 3. IMPLEMENTATION WEAKNESSES

Identify deficiencies in how the EXISTING system is being applied—where adequate regulations exist but are not achieving their safety intent in practice.

CRITICAL: Report ONLY weaknesses supported by the incident evidence. If no implementation weaknesses are evident, state "No implementation
weaknesses identified" and explain why. If many are evident - which is likely - list all of them. The count must be driven entirely by what the
data supports—not by what seems like a "reasonable" number. There is no upper limit, and the total does not have to equal the number of systemic
weaknesses.

For each weakness identified:

1. **Weakness Title**: A concise label (e.g., "Inadequate surveillance of combined-position ATC operations")

2. **Weakness Description**: A detailed technical explanation of the implementation deficiency. Do NOT just restate the title. Explain specifically how the existing regulation is failing to achieve its safety intent in practice, what operational factors contribute to this, and what would need to change. This must be detailed enough that an aviation SME could draft a corrective action. Examples of deficiency types:
   - Regulation is adequate but surveillance/enforcement is insufficient
   - Training requirements exist but training quality or fidelity is inadequate
   - Procedures are compliant on paper but defeat the safety intent in practice
   - Operational pressures lead to normalized deviation from requirements
   - Information flow failures prevent timely compliance (ADs, NOTAMs, safety directives)

3. **Evidence from Incidents**: Cite ALL incident justifications that support this weakness. If multiple incidents demonstrate the same implementation failure, quote or reference each one. Pattern strength matters—a weakness supported by 8 incidents is more significant than one supported by 1.

4. **Regulatory Context**: For each regulation cited, explain specifically what it requires, how it SHOULD prevent this type of failure, and why it is not achieving that intent in practice. Do not simply list citations—explain the gap between regulatory intent and operational reality.

---

Be precise and technical. Generic statements like "procedures weren't followed" are insufficient—explain the STRUCTURAL reason why procedures weren't followed (systemic weakness) or the OPERATIONAL reason why adequate procedures failed to achieve safety (implementation weakness).

## 4. OBSERVABILITY ASSESSMENT

**CRITICAL - VERIFICATION STEP (THINK BEFORE GENERATING)**:
Before writing your analysis, silently verify:
1. List ALL relevant regulations - check against Section 1 and incident data
2. **DEDUPLICATE RIGOROUSLY** - Remove exact matches by CFR number (e.g., if you list 14 CFR §121.703 once, do NOT list it again even with different description). Check each number carefully.
3. VERIFY TEXT - If not 100% certain of regulation text, mark [Verified Intent Only]
4. For duplicate entries, combine into single entry with complete analysis
5. After drafting Section 4, COUNT your entries and verify no CFR number appears twice

**START WITH READER CONTEXT** (output 2-3 paragraphs):
"This section analyzes whether this barrier provides actual system control (mechanical resistance to failure) or merely regulatory compliance (liability assignment after failure). We categorize each regulation and safety system by its observability characteristics—whether the system can detect compliance in real-time (Observable), whether evidence exists but isn't monitored (Latent), or whether compliance relies entirely on human judgment (Nominal).

The goal is not to assign blame, but to identify **actionable opportunities** to strengthen an already safe system. We focus on what we CAN control and improve, not on dwelling on inherently unmeasurable human factors."

### THE OBSERVABILITY TAXONOMY

Categorize each regulation using this three-tier framework:

**OBSERVABLE (Hard Reality - System Controls)**
- **Definition**: Regulations that map directly to quantifiable data points, physical limits, or real-time sensors. The difference between "Safe" and "Unsafe" is binary and measurable.
- **Nature**: Deterministic. The system KNOWS definitively whether the requirement is met.
- **Detection**: Real-time sensor data or deterministic post-hoc measurement.
- **Examples**: MSAW alerts, TCAS RA, altitude deviation >300ft, ASDE-X runway incursion alerts, minimum fuel reserves (quantifiable), RVSM separation minima.
- **System Role**: Acts as a SENSOR—provides mechanical resistance or automated detection of violation.
- **Litmus Test**: "If violated, does a bell ring or a light flash IN REAL-TIME?" If yes → Observable
- **Question**: "Did the barrier hold?" → **Definitive Yes/No with evidence**
- **CRITICAL**: Periodic inspections/audits are NOT Observable—they are Latent. Observable requires real-time or immediate automated detection.

**LATENT (Dark Reality - Blind Spots)**
- **Definition**: Regulations that generate physical or digital evidence (audio recordings, logs, rosters, documents), but where the safety system lacks real-time "eyes" to monitor it. The data exists but the connection is broken—no one is watching.
- **Nature**: Potential observability. Could be monitored but currently is not.
- **Detection**: Evidence exists and can be reviewed post-hoc (accident investigation, spot audits), but is not actively monitored for real-time safety intervention.
- **Examples**: CVR recordings (exist but only reviewed after incidents), duty time rosters (exist but not actively audited for fatigue correlation), standard phraseology compliance (recorded but not monitored), maintenance logbook entries (exist but not continuously verified), training records (documented but application not observed), crew scheduling systems (track duty hours but not monitored for real-time fatigue).
- **System Role**: Acts as a BLIND SPOT—we assume compliance based on absence of failure reports, not presence of safety data.
- **Litmus Test**: "Does evidence exist that could be reviewed after the fact?" If yes → Latent
- **Question**: "Did the barrier hold?" → **"We assume yes because no one reported otherwise"**
- **CRITICAL DISTINCTION**: If objective evidence EXISTS in any form (electronic records, paper logs, audio/video recordings, databases, scheduling systems), it is Latent. "Relies on self-reporting" or "based on oversight" is NOT automatically Nominal—if records exist (even self-reported ones), it's Latent. Flight crew duty time limits are Latent (crew scheduling systems track duty hours). Only pure cognitive/subjective requirements with NO evidence trail whatsoever are Nominal.

**NOMINAL (Soft Reality - Regulatory Fiction)**
- **Definition**: Regulations that rely entirely on cognitive states, judgment, intent, or subjective human performance. They define legal requirements but lack ANY physical proxy that can be detected by a system—even post-hoc. NO evidence trail exists.
- **Nature**: Subjective and unknowable except via inference after consequences manifest.
- **Detection**: Only after failure leads to observable consequences. Cannot be measured directly—no logs, no recordings, no data.
- **Examples**: "Exercise good judgment," "Maintain situational awareness," "Use due care," "Remain vigilant," "Assess and mitigate risk," "Maintain proficiency," "Comply with ATC instructions" (the INTENT to comply, not the act of complying), crew resource management (the QUALITY of communication, not that it occurred).
- **System Role**: REGULATORY, not FUNCTIONAL—explains who is responsible but provides ZERO mechanical resistance to failure.
- **Litmus Test**: "Can this EVER be measured, even after the accident?" If no → Nominal
- **Question**: "Did the barrier hold?" → **"We'll decide after investigating the accident"**
- **CRITICAL DISTINCTION**: If ANY evidence exists (even if not monitored), it is Latent, not Nominal. Nominal means NO evidence trail whatsoever—pure cognitive/subjective states only.

### YOUR OBSERVABILITY ANALYSIS

For EACH regulation listed in Section 1, AND any additional relevant regulations you identify from the incident data or your knowledge base, provide:

**CRITICAL ACCURACY REQUIREMENT**: If you reference a CFR regulation by number, you MUST state its correct title and content. Do not hallucinate or misidentify regulations. If uncertain about a regulation's exact content, do not include it.

**Format for each regulation**:

1. **Regulation Citation AND Title** (e.g., "14 CFR §91.123 - Compliance with ATC clearances and instructions")

2. **Observability Classification**: Observable / Latent / Nominal

3. **Observability Rationale**:
   - **If Observable**: What sensor, system, or quantifiable measurement detects compliance/violation? Is detection real-time or deterministic post-hoc? Is the alert actionable (can intervention prevent the failure)?
   - **If Latent**: What evidence exists? Where is it stored (CVR, logs, rosters, records)? Why is it not actively monitored? What would be required to make it Observable (technology, process, resources)?
   - **If Nominal**: What cognitive state or subjective judgment does it require? Why is there no physical proxy? Can this EVER be made observable, or is it inherently subjective?

4. **Failure Detection Mechanism**:
   - Observable: Real-time alert / Deterministic post-hoc measurement
   - Latent: Post-hoc evidence review (investigation, audit)
   - Nominal: Consequence-based inference only

5. **Enforcement Mechanism**:
   - Observable: Physical (system prevents violation) / Automated (system alerts, human intervenes)
   - Latent: Audit-based (random checks, post-incident review)
   - Nominal: Legal (post-violation penalty only)

### SAFETY SYSTEMS AND TECHNOLOGIES (Beyond CFR Regulations)

In addition to analyzing CFR regulations, identify Observable/Latent elements mentioned in the incident data that represent actual safety systems, even if not codified in regulations. These are the REAL-WORLD controls that exist in the operational environment.

**Common safety systems to look for in incidents**:
- ASDE-X / ASDE-3 (runway incursion detection)
- CVR / FDR (cockpit/flight data recorders)
- TCAS / EGPWS (collision/terrain avoidance)
- Tower radar displays
- Visual surveillance by controllers
- Electronic flight strips / position awareness systems
- Crew scheduling systems
- Training records databases
- Airport lighting systems
- NOTAMs and briefing systems

For each system mentioned in incidents, provide:

1. **System Name and Function**
2. **Classification**: Observable / Latent
3. **Evidence from Incidents**: Quote where it's mentioned and how it performed
4. **Why It Failed** (if Observable but ineffective): Alert not heard? Alert too late? Human ignored it?
5. **Why Not Monitored** (if Latent): Technology exists, why no active monitoring?

**CRITICAL**: Include this analysis in the final Barrier Composition calculation. The barrier consists of BOTH CFR regulations AND actual safety systems.

### BARRIER COMPOSITION

**CRITICAL - OUTPUT COUNTS, NOT PERCENTAGES**: LLMs are not reliable at arithmetic. Provide raw counts only.

After categorizing all regulations and safety systems, provide the composition in this format:

**Barrier Composition**: X Observable, Y Latent, Z Nominal (Total: N elements)

Immediately below the counts, enumerate the elements behind them, grouped by classification (e.g., "Latent: §139.329; Subpart D self-inspection; vehicle radios; ..."), so a reader can verify the totals against the classifications above. Count each element exactly once; a grouped citation (e.g., §§91.126-129) counts as one element unless you classified its parts separately.

Then provide a brief interpretation explaining what this composition means for the barrier's effectiveness without calculating percentages.

### BARRIER TYPE ASSESSMENT

Based on the raw counts (percentages will be calculated automatically by the system):

**Barrier Type**:
- If Observable dominates: **Control Barrier** (Hard Safety) - Provides mechanical resistance or active monitoring
- If mixed distribution: **Hybrid Barrier** - Some system controls, some compliance reliance
- If Nominal dominates: **Hollow Barrier** (Paper Safety) - Primarily regulatory compliance with minimal mechanical resistance

**CRITICAL**: State explicitly which type this barrier is. Be brutally honest - most aviation regulations are Nominal. Do not pretend a regulation is Observable just because it is written down. **If a pilot can ignore it without a bell ringing, it is Nominal.**

### POINT OF IRREVERSIBILITY ANALYSIS

**CRITICAL - ANCHOR TO SPECIFIC TIMELINE**: Identify the precise moment in typical incident sequences where Observable/Latent controls cease to be effective.

**Format**:

**Transition Event**: [Specific action/decision point in incident sequence]

**What the System Saw**: [Observable/Latent data available at this moment]

**What the System Missed**: [Nominal factors that took over]

**Evidence from Incidents**: [Quote 2-3 specific incidents showing this exact transition point]

**Example**:
**Transition Event**: Controller clears aircraft for takeoff while another aircraft is on the runway
**What the System Saw**: ASDE-X detected position conflict, alerted controller (Observable control exists but was ignored due to workload)
**What the System Missed**: Controller's cognitive state (attention tunneling, strip management failure) prevented hearing/processing alert
**Evidence**: "I had the strip up that [Ops Vehicle] was on the runway but did not see it" (ASRS-1748105)

### IMPROVEMENT OPPORTUNITIES - ACTIONABLE RECOMMENDATIONS

**CRITICAL - BE PROACTIVE, NOT DEFEATIST**: Focus on what we CAN do to strengthen the system. Do not waste text on "human factors are unknowable" - we know that. Focus on **actionable interventions**.

Provide 3-5 **specific, actionable** recommendations using this exact format:

---

**Recommendation #[N]**: [Specific technical/procedural/training intervention]

**Current → Target State**: [Observable: X] → [Observable: Y] (or Latent → Observable, Nominal → Latent, etc.)

**Why This Element is Currently Dark**: State the ONE reason that best fits - inherently subjective (can never be measured), lacks technology (could be measured with future R&D), or lacks implementation (approach exists but is not deployed) - and justify it in a sentence. Never list all three; an element cannot be all of them at once, and selecting everything signals the analysis was not done.

**How to Illuminate It**: [Specific action: deploy X technology, implement Y procedure, enhance Z training, increase enforcement visibility of W]

**Feasibility**: High / Medium / Low [Explain: technology maturity, organizational readiness, operational complexity]

**Impact**: [Based on incident data: "XX incidents in this dataset show pattern X that this would address. Quote specific incident evidence."]

---

**For Nominal Regulations That Cannot Be Made Observable**:

Identify specific, actionable interventions to strengthen compliance where no technical solution exists:
- Training enhancements: What specific scenarios, procedures, or communication patterns should be emphasized?
- Enforcement mechanisms: What makes non-compliance visible and consequential?
- Procedural forcing functions: What mandatory callouts, checklists, or confirmation requirements can be added?
- Operational changes: What environmental/procedural modifications reduce cognitive load on critical tasks?

Base recommendations on incident evidence showing exactly WHERE and WHY Nominal regulations failed.

### CRITICAL OBSERVABILITY INSTRUCTIONS

- **Be ruthlessly honest**: If a barrier is Hollow (mostly Nominal), say so explicitly - but focus on what to DO about it
- **Use incident evidence**: Quote specific incidents to support classifications and recommendations
- **Provide counts, not math**: Output raw counts in JSON format - do NOT calculate percentages
- **Be actionable**: Every weakness identified should have a corresponding improvement opportunity
- **Quantify impact**: Use incident patterns to show how many incidents each recommendation would address
- **Identify what works**: Analyze why Nominal regulations are effective when they work - this guides strengthening strategies
- **Focus on control**: What CAN we do, not what we can't measure
- **NO cost, time, or ROI analysis**: Do not discuss cost, timeline to implement, ROI, or priority tiers - these are out of scope

---

**EXAMPLE OBSERVABILITY ASSESSMENT** (for reference, do not copy verbatim):

*14 CFR §91.123 (Compliance with ATC clearances)*
- **Classification**: Nominal
- **Rationale**: Requires pilot to "comply with ATC instructions"—this is a cognitive judgment and behavioral expectation. No sensor detects whether the pilot INTENDS to comply or is ABLE to comply. Readback can be recorded (Latent) but actual compliance (vs. just acknowledgment) is unobservable until consequences manifest.
- **Failure Detection**: Consequence-based inference (runway incursion occurred, therefore non-compliance)
- **Enforcement**: Legal penalty post-violation
- **Observability Horizon**: Crossed when pilot makes mental decision to enter runway. System cannot see pilot intent, fatigue, distraction, or risk assessment.

*ASDE-X Runway Incursion Alert (if applicable)*
- **Classification**: Observable
- **Rationale**: Automated sensor detects aircraft/vehicle position relative to runway safety areas. Alerts controller in real-time when incursion detected.
- **Failure Detection**: Real-time alert
- **Enforcement**: Automated alert → controller intervention (if heeded)
- **Observability Horizon**: System sees position, but cannot see if controller will hear/act on alert. Multiple incidents show "ASDE alerted but I didn't hear it"—the Observable control exists but the human interface failed.

---

Be precise, technical, and unflinching in your assessment. The goal is to reveal the TRUE nature of this barrier—whether it provides mechanical safety control or merely assigns liability after failures occur.

---

CRITICAL INSTRUCTIONS:
- START from the incident justifications. What type of operations do they describe?
- Only include regulations that apply to those operations
- 14 CFR §107 is for UNMANNED aircraft—do not cite it for manned aircraft incidents
- If Daedalus regulations don't apply, say so and provide the regulations that DO apply
- The audience knows aviation regulations—when Daedalus provides the text, include it in full rather than summarizing; when it does not, follow the grounding rules (own words, marked unverified)
- This is NOT about identifying "patterns" or "themes"—it's about regulatory gaps`;

            // Start the backend run; real progress and partial output arrive via
            // polling, so switching tabs or views and coming back resumes cleanly.
            const runId = await CfrService.startAnalysis({
                nodeId,
                searchQuery,
                prompt: summarizationPrompt
            });
            storeApi.getState().setNodeCfrRunId(nodeId, runId);
            pollAnalysisRun(nodeId, runId);
        } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error('CFR lookup failed:', error);
            if (cfrPollIntervalRef.current) {
                clearInterval(cfrPollIntervalRef.current);
                cfrPollIntervalRef.current = null;
            }
            thread = [...thread, { role: 'system', content: `CFR lookup failed: ${error.message || 'Unknown error'}` }];
            storeApi.getState().setNodeCfrHistory(nodeId, thread as PersistedChatMessage[]);
            storeApi.getState().setNodeCfrStatus(nodeId, 'error');
        }
    }, [node, nodeId, storeApi, ineffectiveReports, effectiveReports, pollAnalysisRun]);

    // Auto-run the CFR lookup once per node, eagerly on node select, but only for
    // nodes that have at least one mapped report. getNodeCfrStatus is read fresh from
    // the store so the guard survives StrictMode double-invoke and re-renders.
    useEffect(() => {
        if (!nodeId || loading) return;
        if (reports.length < 1) return;
        if (storeApi.getState().getNodeCfrStatus(nodeId) !== undefined) return;
        void runCfrLookup(false);
    }, [nodeId, loading, reports.length, runCfrLookup, storeApi]);

    // Resume polling for a run left in-flight when the user switched tabs or
    // views. The runId is cached per node in the store; the latest-run endpoint
    // covers clients that lost it entirely (e.g. a page reload). No cleanup on
    // node change: an in-flight poll keeps writing to its own node's store entry
    // so a finished result still lands while the user is elsewhere; starting a
    // new poll replaces the shared interval, and the server keeps the run state
    // for any poll that gets displaced.
    useEffect(() => {
        if (!nodeId) return;
        if (storeApi.getState().getNodeCfrStatus(nodeId) !== 'running') return;
        const runId = storeApi.getState().getNodeCfrRunId(nodeId);
        if (runId) {
            pollAnalysisRun(nodeId, runId);
        } else {
            void CfrService.getLatestAnalysis(nodeId)
                .then((snapshot): void => {
                    storeApi.getState().setNodeCfrRunId(nodeId, snapshot.runId);
                    pollAnalysisRun(nodeId, snapshot.runId);
                })
                .catch((): void => {
                    storeApi.getState().setNodeCfrHistory(nodeId, [
                        { role: 'system', content: 'CFR analysis is no longer available on the server. Use Re-run to start a new one.' }
                    ] as PersistedChatMessage[]);
                    storeApi.getState().setNodeCfrStatus(nodeId, 'error');
                });
        }
    }, [nodeId, pollAnalysisRun, storeApi]);

    const isCfrRunning = cfrStatus === 'running';

    const handleCfrRerun = useCallback((): void => {
        void runCfrLookup(true);
    }, [runCfrLookup]);

    // Re-run control shown in the CFR thread header
    const cfrRerunButton = (
        <Button
            size="small"
            variant="outlined"
            onClick={handleCfrRerun}
            disabled={isCfrRunning || !node}
            startIcon={isCfrRunning ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
            sx={{ ml: 1, fontSize: '0.7rem', py: 0.25, minHeight: 0 }}
        >
            {isCfrRunning ? 'Searching...' : (cfrMessages.length > 0 ? 'Re-run' : 'Run CFR Lookup')}
        </Button>
    );

    // Don't render if no node selected
    if (!node) {
        return null;
    }

    // Wrapper styles for flex height propagation
    const wrapperSx = fillHeight ? {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden'
    } : {};

    // CFR thread content: idle prompt before any run, otherwise the chat dialog.
    const cfrContent = cfrMessages.length === 0 ? (
        <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            p: 3,
            textAlign: 'center'
        }}>
            <Typography variant="body2" color="text.secondary">
                {reports.length < 1
                    ? 'No mapped reports for this node yet. You can still run the CFR analysis from the node definition.'
                    : 'Preparing CFR analysis...'}
            </Typography>
            <Button
                size="small"
                variant="outlined"
                onClick={handleCfrRerun}
                disabled={isCfrRunning}
                startIcon={isCfrRunning ? <CircularProgress size={12} /> : undefined}
            >
                {isCfrRunning ? 'Searching...' : 'Run CFR Lookup'}
            </Button>
        </Box>
    ) : (
        <LLMChatDialog
            title="CFR Analysis"
            contextMessage={`Regulatory analysis for: ${node.name || 'Unknown'}`}
            maxHeight={fillHeight ? undefined : 450}
            headerActions={cfrRerunButton}
            fillHeight={fillHeight}
            headerIconsPaddingRight={4}
            messages={cfrMessages as ChatMessage[]}
            onMessagesChange={handleCfrMessagesChange}
        />
    );

    // General-chat content (original behaviour)
    const nodeNoun = nodeNounFor(node);
    const suggestedPrompts = suggestedPromptsFor(node);
    const chatContent = (
        <LLMChatDialog
            title={`Ask AI about this ${nodeNoun}`}
            contextPrepend={contextPrepend}
            contextMessage={contextMessage}
            maxHeight={fillHeight ? undefined : 450}
            fillHeight={fillHeight}
            headerIconsPaddingRight={4}
            suggestedPrompts={suggestedPrompts}
            messages={persistedMessages as ChatMessage[]}
            onMessagesChange={handleMessagesChange}
            contextSent={persistedContextSent}
            onContextSentChange={handleContextSentChange}
        />
    );

    return (
        <Box sx={wrapperSx}>
            {/* Sub-tab switch: CFR Analysis (default) vs general Chat */}
            <Tabs
                value={activeSubTab}
                onChange={(_e, value: SubTab): void => setActiveSubTab(value)}
                variant="fullWidth"
                sx={{ minHeight: 36, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
            >
                <Tab
                    value="chat"
                    label="Chat"
                    sx={{ minHeight: 36, fontSize: '0.75rem', textTransform: 'none' }}
                />
                <Tab
                    value="cfr"
                    label={
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            CFR Analysis
                            {isCfrRunning && <CircularProgress size={12} color="inherit" />}
                        </Box>
                    }
                    sx={{ minHeight: 36, fontSize: '0.75rem', textTransform: 'none' }}
                />
            </Tabs>

            {/* Both threads stay mounted (display-toggled) so in-flight state survives a tab switch */}
            <Box sx={{
                display: activeSubTab === 'cfr' ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                pt: 1
            }}>
                {cfrContent}
            </Box>
            <Box sx={{
                display: activeSubTab === 'chat' ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                pt: 1
            }}>
                {/* Over-limit opt-in, only meaningful before the first question (context is sent once) */}
                {reportsWithJustifications.length > REPORT_LIMIT && !persistedMessages.some(m => m.role !== 'system') && (
                    <FormControlLabel
                        sx={{ px: 1, mb: 0.5, mr: 0 }}
                        control={
                            <Checkbox
                                size="small"
                                checked={includeAllReports}
                                onChange={(e): void => setIncludeAllReports(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="caption" color="text.secondary">
                                Send all {reportsWithJustifications.length} reports to the AI (default sends the first {REPORT_LIMIT})
                            </Typography>
                        }
                    />
                )}
                {chatContent}
            </Box>
        </Box>
    );
};

export default React.memo(JustificationAnalysisPanel);
